import React from "react";
import { Alert, Spin, Table } from "antd";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
} from "recharts";

import { hoursReadable } from "../../utils";
import { REDMINE_URL } from "../../constants";
import { useProjectDevelopmentMetrics } from "../../hooks/useProjectDevelopmentMetrics";

interface ProjectDevelopmentMetricsProps {
  project_id: number;
  fromDate: Date;
  toDate: Date;
}

export const ProjectDevelopmentMetrics = ({
  project_id,
  fromDate,
  toDate,
}: ProjectDevelopmentMetricsProps) => {
  const {
    stats,
    expandedRowKeys,
    handleExpand,
    loadingMetrics,
    fetchMetricsError,
  } = useProjectDevelopmentMetrics(project_id, fromDate, toDate);

  const estimatedTime = stats?.estimated_time || 0;
  const totalEstimatedTime = stats?.total_estimated_time || 0;
  const developmentTime = stats?.development_time || 0;

  const barChartData = [
    {
      name: "Estimated Time",
      hours: estimatedTime || totalEstimatedTime,
    },
    { name: "Spent Time", hours: developmentTime },
  ];

  const details = stats?.details ?? [];

  if (loadingMetrics && !stats) {
    return (
      <div className="w-full h-full flex items-center justify-center py-16 bg-gray-50">
        <Spin size="large" />
      </div>
    );
  }

  if (fetchMetricsError) {
    return (
      <div className="px-12 py-8 bg-gray-50">
        <Alert
          type="error"
          showIcon
          message="Project metrics could not be loaded."
          description={fetchMetricsError}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">Estimated</p>
          <p className="text-xl font-semibold text-gray-800">
            {hoursReadable(estimatedTime)}
          </p>
        </div>

        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">Total Estimated (tasks)</p>
          <p className="text-xl font-semibold text-gray-800">
            {hoursReadable(totalEstimatedTime)}
          </p>
        </div>

        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">Spent</p>
          <p className="text-xl font-semibold text-gray-800">
            {hoursReadable(developmentTime)}
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">
          Time Comparison
        </h3>

        <div className="w-full h-[160px] max-w-xl">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={barChartData}
              layout="vertical"
              margin={{ top: 5, right: 40, bottom: 5, left: 5 }}
              barSize={30}
            >
              <YAxis
                dataKey="name"
                type="category"
                tick={{ fill: "#6B7280", fontSize: 13 }}
                width={120}
              />
              <XAxis
                type="number"
                hide
                domain={[
                  0,
                  Math.max(
                    estimatedTime || totalEstimatedTime,
                    developmentTime,
                  ),
                ]}
              />

              <Tooltip
                formatter={(v: number) => hoursReadable(v)}
                cursor={{ fill: "rgba(99,102,241,0.08)" }}
              />

              <Bar dataKey="hours" fill="#6366F1" radius={[8, 8, 8, 8]}>
                <LabelList
                  dataKey="hours"
                  position="right"
                  formatter={(v: number) => hoursReadable(v)}
                  style={{ fill: "#374151", fontWeight: 500 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {estimatedTime === 0 && (
          <p className="text-xs text-gray-400 mt-3">
            Estimated time calculated from subtasks.
          </p>
        )}
      </div>

      {/* Table */}
      {details.length > 0 && (
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Development Breakdown
          </h3>

          <Table
            dataSource={details}
            loading={loadingMetrics}
            rowKey={(record) => record.id.toString()}
            rowClassName={() => "hover:bg-gray-50 transition-colors"}
            pagination={false}
            size="small"
            expandable={{
              expandedRowKeys,
              onExpand: handleExpand,
            }}
            className="rounded-lg"
            columns={[
              {
                title: "Task",
                dataIndex: "id",
                key: "id",
                width: "60%",
                render: (id, record) => {
                  const isInternal =
                    record.tracker === "Contract" || record.tracker === "Epic";

                  const linkHref = isInternal
                    ? `/projects/${id}?chart=developmentMetrics`
                    : `${REDMINE_URL}/issues/${id}`;

                  return (
                    <div className="flex flex-col">
                      <a
                        href={linkHref}
                        target={isInternal ? "_self" : "_blank"}
                        rel="noreferrer"
                        className="font-semibold text-indigo-600 hover:text-indigo-800"
                      >
                        {`${record.tracker} #${id}`}
                      </a>
                      <span className="text-gray-600 text-sm">
                        {record.subject}
                      </span>
                    </div>
                  );
                },
              },
              {
                title: "Estimated",
                dataIndex: "estimated_hours",
                align: "right",
                width: 120,
                render: (value) => (
                  <span className="font-medium text-gray-700">
                    {hoursReadable(value)}
                  </span>
                ),
              },
              {
                title: "Spent",
                dataIndex: "registered_hours",
                align: "right",
                width: 120,
                render: (value) => (
                  <span className="font-medium text-gray-700">
                    {hoursReadable(value)}
                  </span>
                ),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectDevelopmentMetrics;
